<template>
  <BaseModal
    :model-value="modelValue"
    :title="agreement.terms.title"
    :description="$t('common.legalAgreement.readFullAgreement')"
    max-width-class="max-w-3xl"
    center-on-mobile
    @update:model-value="onClose"
  >
    <div class="space-y-4">
      <p
        class="dark:text-content-level-300 text-content-level-700 text-sm"
        data-testid="agreement-version"
      >
        {{ $t('common.legalAgreement.version', { date: formatApiDate(agreement.terms.version) }) }}
      </p>

      <p
        v-if="signature.isSigned"
        class="dark:text-content-level-300 text-content-level-700 text-sm"
        data-testid="agreement-signed"
      >
        {{
          $t('common.legalAgreement.signedBy', {
            name: signature.name,
            date: formatApiDate(signature.date)
          })
        }}
      </p>

      <div
        ref="scrollContainer"
        class="border border-border-level-200 dark:border-border-inverse/10 max-h-[50vh] overflow-y-auto p-4 rounded-lg"
        data-testid="agreement-scroll"
        role="region"
        tabindex="0"
        :aria-label="$t('common.legalAgreement.scrollRegionLabel')"
        :aria-describedby="isScrollHintVisible ? scrollHintId : undefined"
        @scroll="check"
      >
        <PartnerAgreementBody
          v-if="agreement.type === 'partner_agreement'"
          :terms="agreement.terms"
          show-title
        />

        <LegalClausesBody
          v-else
          :terms="agreement.terms"
        />
      </div>

      <div
        v-if="actionProblem !== ''"
        role="alert"
        data-testid="agreement-action-problem"
      >
        <UAlert
          color="error"
          variant="subtle"
          :description="actionProblem"
        />
      </div>

      <LegalAgreementEmailAction :legal-agreement-id="agreement.id" />
    </div>

    <template #footer>
      <div class="flex flex-wrap gap-3 items-center justify-between w-full">
        <LegalAgreementDocumentActions :legal-agreement-id="agreement.id" />

        <p
          v-if="isScrollHintVisible"
          :id="scrollHintId"
          class="dark:text-content-level-300 text-content-level-700 text-sm"
          data-testid="agreement-scroll-hint"
        >
          {{ $t('common.legalAgreement.scrollHint') }}
        </p>

        <div class="flex flex-wrap gap-3 ml-auto">
          <UButton
            type="button"
            color="neutral"
            variant="outline"
            @click="onClose"
          >
            {{ $t('common.legalAgreement.cancel') }}
          </UButton>

          <UButton
            v-if="canAccept"
            type="button"
            color="primary"
            :disabled="!isAcceptEnabled"
            :loading="isAccepting"
            @click="onAccept"
          >
            {{ $t('common.legalAgreement.iAccept') }}
          </UButton>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, useId, useTemplateRef, watch } from 'vue'
import UAlert from '@nuxt/ui/components/Alert.vue'
import UButton from '@nuxt/ui/components/Button.vue'
import LegalAgreementEmailAction from '~/components/shared/legal/LegalAgreementEmailAction.vue'
import LegalAgreementDocumentActions from '~/components/shared/legal/LegalAgreementDocumentActions.vue'
import LegalClausesBody from '~/components/shared/legal/LegalClausesBody.vue'
import BaseModal from '~/components/shared/dialogs/BaseModal.vue'
import PartnerAgreementBody from '~/design-system/organisms/PartnerAgreementBody/PartnerAgreementBody.organism.vue'
import { useLegalAgreementAcceptance } from '~/composables/useLegalAgreementAcceptance'
import { useScrolledToBottom } from '~/composables/useScrolledToBottom'
import type { LegalAgreement } from '~/composables/LegalAgreement'
import { formatApiDate } from '~/utils/formatApiDate'

const props = defineProps<{
  /** Whether the agreement modal is visible. */
  modelValue: boolean
  /** The agreement shown; its status decides whether scroll-to-accept and I Accept appear. */
  agreement: LegalAgreement
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  /** The agreement was accepted; carries it as accepted. */
  accepted: [agreement: LegalAgreement]
}>()

const { accept, actionProblem, isAccepting } = useLegalAgreementAcceptance()

const canAccept = computed(() => props.agreement.status === 'pending')

type Signature = { date: string; isSigned: true; name: string } | { isSigned: false }

const signature = computed<Signature>(() =>
  'accepted_at' in props.agreement
    ? {
        date: props.agreement.accepted_at,
        isSigned: true,
        name: props.agreement.signatory.name
      }
    : { isSigned: false }
)

const scrollContainer = useTemplateRef<HTMLElement>('scrollContainer')
const { check, hasScrolledToBottom, reset } = useScrolledToBottom(scrollContainer)

const scrollHintId = useId()
const isScrollHintVisible = computed(() => canAccept.value && !hasScrolledToBottom.value)
const isAcceptEnabled = computed(() => hasScrolledToBottom.value && !isAccepting.value)

// The scroll container mounts with the modal body, so the first measurement waits for it.
watch(scrollContainer, (element) => {
  if (element !== null) check()
})

watch(
  () => props.agreement.id,
  () => {
    if (scrollContainer.value !== null) scrollContainer.value.scrollTop = 0
    reset()
    check()
  },
  { flush: 'post' }
)

watch(
  () => props.modelValue,
  (open) => {
    if (open) reset()
  }
)

const onClose = () => {
  emit('update:modelValue', false)
}

const onAccept = async () => {
  const outcome = await accept(props.agreement.id)
  if (!outcome.isAccepted) return
  emit('accepted', outcome.agreement)
  emit('update:modelValue', false)
}
</script>
